import type { Prompts } from "@ant-design/x";
import type { MessageInfo } from "@ant-design/x-sdk";
import type { GetProp } from "antd";

import { Dispatch, PropsWithChildren, ReactNode, SetStateAction } from "react";

import { Profile } from "~/services/agent/get-profile/";

export const DATE = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_30_DAYS: "last_30_days",
  EARLIER: "earlier",
};

export type ConversationListItem = {
  key: string;
  label: ReactNode;
  lastActivityAt?: string;
  group?: string;
};

/**
 * Message object structure for chat communication
 */
export interface Message {
  /** Message role (user, assistant, system) */
  role: "user" | "assistant" | "system";
  /** The actual message content */
  content: string | { text?: string; ext_text?: string };
  /** Optional message type for bot responses */
  type?: string;
  /** Optional session identifier to group related messages */
  session_id?: string;
}

/**
 * Popup Component States
 */
export interface PopupStates {
  profileData: Profile;
  content: string;
  provider: any; // SSEChatProvider
  contextHolder: ReactNode;
  conversationItems: Array<ConversationListItem>;
  curConversationId?: string;
  messages: Array<MessageInfo<Message>>;
  sidebarOpen: boolean;
  isRequesting: boolean;
  processingSteps: string[];
  followUpQueries: string[];
  hasMenu: boolean;
  quickQuestions: string[];
}

/**
 * Popup Component Events
 */
export interface PopupEvents {
  onSubmit: (content: string) => void;
  setContent: Dispatch<SetStateAction<string>>;
  handleActiveChange: (val: string) => Promise<void>;
  handleAddConversation: () => Promise<void>;
  resetConversationState: () => void;
  onPromptsItemClick: GetProp<typeof Prompts, "onItemClick">;
  handleOpenSidebar: () => void;
  handleCloseSidebar: () => void;
  handleCancel: () => void;
  onReload: () => Promise<void>;
  onClose?: () => void;
}

export type PopupContext = {
  states: PopupStates;
  events: PopupEvents;
};

export type PopupProviderProps = PropsWithChildren<{
  onClose?: () => void;

  hasMenu?: boolean;
  quickQuestions?: string[];

  /**
   * Resume an existing conversation by providing its session ID.
   * When supplied the widget will load the session's history on mount.
   */
  session_id?: string;

  /**
   * Called whenever a new session is created by the backend so the host can
   * persist the ID and pass it back on the next page load.
   */
  onSessionChange?: (sessionId?: string) => void;
}>;
